type DraftStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export interface TemplateDraftKeyParts {
  area: string;
  tenantId?: string | null;
  userId?: string | null;
  resourceId?: string | null;
}

export interface TemplateDraftRecord<TPayload = unknown> {
  savedAt: string;
  payload: TPayload;
}

export function createTemplateDraftKey({
  area,
  tenantId,
  userId,
  resourceId,
}: TemplateDraftKeyParts): string {
  return [
    'medd-sim',
    'draft',
    area,
    tenantId || 'no-tenant',
    userId || 'anonymous',
    resourceId || 'new',
  ].join(':');
}

export function saveTemplateDraft<TPayload>(
  storage: DraftStorage | undefined,
  key: string,
  payload: TPayload
): TemplateDraftRecord<TPayload> | null {
  if (!storage) return null;

  const record: TemplateDraftRecord<TPayload> = {
    savedAt: new Date().toISOString(),
    payload,
  };

  try {
    storage.setItem(key, JSON.stringify(record));
    return record;
  } catch {
    return null;
  }
}

export function loadTemplateDraft<TPayload = unknown>(
  storage: DraftStorage | undefined,
  key: string,
  options: { maxAgeDays?: number } = {}
): TemplateDraftRecord<TPayload> | null {
  if (!storage) return null;

  try {
    const raw = storage.getItem(key);
    if (!raw) return null;

    const record = JSON.parse(raw) as TemplateDraftRecord<TPayload>;
    const savedAt = new Date(record.savedAt);
    if (!record.payload || Number.isNaN(savedAt.getTime())) {
      storage.removeItem(key);
      return null;
    }

    if (options.maxAgeDays) {
      const maxAgeMs = options.maxAgeDays * 24 * 60 * 60 * 1000;
      if (Date.now() - savedAt.getTime() > maxAgeMs) {
        storage.removeItem(key);
        return null;
      }
    }

    return record;
  } catch {
    storage.removeItem(key);
    return null;
  }
}

export function clearTemplateDraft(storage: DraftStorage | undefined, key: string): void {
  if (!storage) return;

  try {
    storage.removeItem(key);
  } catch {
    // Draft cleanup should never block the main workflow.
  }
}
